import { Decorator } from 'final-form'
import createDecorator from 'final-form-calculate'
import get from 'lodash/get'

import { OrderCreateOperationForm } from '../components/OrderCreatePointOperation/types'
import { TOrderCreateFormData } from '../types'

export interface TerminalSyncFormData {
  operation?: OrderCreateOperationForm[]
}

export interface TerminalSyncState {
  lastOperations: OrderCreateOperationForm[] | undefined
  lastTerminalId: string | undefined
}

export function resetTerminalSyncState(state: TerminalSyncState) {
  state.lastOperations = undefined
  state.lastTerminalId = undefined
}

export function createTerminalSyncDecorators<TFormData extends TerminalSyncFormData = TOrderCreateFormData>(
  pointType: 'departure' | 'destination',
  state: TerminalSyncState
) {
  const prefix = `${pointType}Point`
  const terminalIdField = `${prefix}.terminalId`
  const isCourierField = `${prefix}.isCourier`
  const addressDetailField = `${prefix}.addressDetail`

  const handleTerminalIdChange = (
    value: string | undefined,
    _field: string,
    allValues?: TFormData
  ): Partial<TFormData> => {
    const { operation } = allValues || {}
    const isCourier = get(allValues, `${prefix}.isCourier`)
    let operationPoint: OrderCreateOperationForm[] | undefined

    if (value && state.lastOperations?.length && state.lastTerminalId && !isCourier) {
      operationPoint = state.lastOperations.map((operationItem) => ({
        ...operationItem,
        terminalId: value
      }))
      state.lastTerminalId = value
      state.lastOperations = undefined

      return {
        operation: operation?.concat(operationPoint || [])
      } as Partial<TFormData>
    }

    if (value) {
      operationPoint = operation?.map((operationItem: OrderCreateOperationForm) =>
        operationItem.terminalId &&
        operationItem.terminalId === state.lastTerminalId &&
        operationItem.pointType === pointType
          ? {
              ...operationItem,
              terminalId: value
            }
          : operationItem
      )

      state.lastTerminalId = value

      return {
        operation: operationPoint
      } as Partial<TFormData>
    }

    if (operation?.length && !state.lastOperations?.length && state.lastTerminalId && !isCourier) {
      operationPoint = operation?.filter(
        (operationItem: OrderCreateOperationForm) =>
          !(operationItem.terminalId === state.lastTerminalId && operationItem.pointType === pointType)
      )
      state.lastOperations = operation?.filter(
        (operationItem: OrderCreateOperationForm) =>
          operationItem.terminalId === state.lastTerminalId && operationItem.pointType === pointType
      )

      return {
        operation: operationPoint
      } as Partial<TFormData>
    }

    return {}
  }

  const handleIsCourierChange = (
    value: boolean | undefined,
    _field: string,
    allValues?: TFormData
  ): Partial<TFormData> => {
    const { operation } = allValues || {}
    let operationPoint: OrderCreateOperationForm[] | undefined

    if (value || (value === undefined && state.lastTerminalId)) {
      if (operation?.length && !state.lastOperations?.length) {
        operationPoint = operation?.filter(
          (operationItem: OrderCreateOperationForm) =>
            !(operationItem.terminalId === state.lastTerminalId && operationItem.pointType === pointType)
        )
        state.lastOperations = operation?.filter(
          (operationItem: OrderCreateOperationForm) =>
            operationItem.terminalId === state.lastTerminalId && operationItem.pointType === pointType
        )

        return {
          [addressDetailField]: undefined,
          [terminalIdField]: undefined,
          operation: operationPoint
        } as Partial<TFormData>
      }

      return {
        [addressDetailField]: undefined,
        [terminalIdField]: undefined
      } as Partial<TFormData>
    }

    return {}
  }

  return [
    createDecorator({
      field: terminalIdField,
      updates: handleTerminalIdChange as any
    }) as unknown as Decorator<TFormData>,
    createDecorator({
      field: isCourierField,
      updates: handleIsCourierChange as any
    }) as unknown as Decorator<TFormData>
  ]
}
